import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchAdminPlans = createAsyncThunk(
  "plans/fetchAdminPlans",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/plans", { params });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch plans");
    }
  }
);

export const fetchPlansConfig = createAsyncThunk(
  "plans/fetchPlansConfig",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/superAdmin/plans/config");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const createPlan = createAsyncThunk(
  "plans/createPlan",
  async (planData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/plans", planData);
      dispatch(showToast({ message: "Plan created successfully", variant: "success" }));
      console.log(response.data,"Response from create plan");
      
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to create plan", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updatePlan = createAsyncThunk(
  "plans/updatePlan",
  async ({ planId, ...planData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/superAdmin/plans/${planId}`, planData);
      dispatch(showToast({ message: "Plan updated successfully", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to update plan", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const deletePlan = createAsyncThunk(
  "plans/deletePlan",
  async ({ planId, force = false }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/superAdmin/plans/${planId}?force=${force}`);
      dispatch(showToast({ message: "Plan deleted successfully", variant: "success" }));
      return planId;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to delete plan";
      dispatch(showToast({ message: msg, variant: "danger" }));
      return rejectWithValue(msg);
    }
  }
);

export const togglePlanStatus = createAsyncThunk(
  "plans/togglePlanStatus",
  async (planId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/superAdmin/plans/${planId}/toggle`);
      dispatch(showToast({ message: response.data.message, variant: "success" }));
      return response.data.plan;
    } catch (err) {
      dispatch(showToast({ message: "Failed to toggle plan status", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const reorderPlans = createAsyncThunk(
  "plans/reorderPlans",
  async (orderedIds, { rejectWithValue }) => {
    try {
      await api.put("/superAdmin/plans/reorder", { orderedIds });
      return orderedIds;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateCommonFeatures = createAsyncThunk(
  "plans/updateCommonFeatures",
  async (commonFeatures, { rejectWithValue, dispatch }) => {
    try {
      await api.put("/superAdmin/plans/common-features", { commonFeatures });
      dispatch(showToast({ message: "Common features updated", variant: "success" }));
      return commonFeatures;
    } catch (err) {
      dispatch(showToast({ message: "Failed to update common features", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const pauseUserSubscription = createAsyncThunk(
  "plans/pauseUserSubscription",
  async ({ userId, pauseDurationDays }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/plans/subscription/pause", { userId, pauseDurationDays });
      dispatch(showToast({ message: response.data.message, variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to pause subscription", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const resumeUserSubscription = createAsyncThunk(
  "plans/resumeUserSubscription",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/plans/subscription/resume", { userId });
      dispatch(showToast({ message: response.data.message, variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to resume subscription", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const cancelUserSubscription = createAsyncThunk(
  "plans/cancelUserSubscription",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/plans/subscription/cancel", { userId });
      dispatch(showToast({ message: response.data.message || "Subscription cancelled", variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to cancel subscription", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateGlobalTrialPeriod = createAsyncThunk(
  "plans/updateGlobalTrialPeriod",
  async (durationDays, { rejectWithValue, dispatch }) => {
    try {
      await api.put("/superAdmin/plans/trial/global", { durationDays });
      dispatch(showToast({ message: `Global trial period set to ${durationDays} days`, variant: "success" }));
      return durationDays;
    } catch (err) {
      dispatch(showToast({ message: "Failed to update global trial period", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateUserTrialPeriod = createAsyncThunk(
  "plans/updateUserTrialPeriod",
  async ({ userId, durationDays }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/superAdmin/plans/trial/user/${userId}`, { durationDays });
      dispatch(showToast({ message: response.data.message, variant: "success" }));
      return response.data;
    } catch (err) {
      dispatch(showToast({ message: "Failed to update user trial period", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const pauseUserAccess = createAsyncThunk(
  "plans/pauseUserAccess",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/plans/access/pause", { userId });
      dispatch(showToast({ message: response.data.message || "User access paused", variant: "success" }));
      return userId;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to pause access", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const resumeUserAccess = createAsyncThunk(
  "plans/resumeUserAccess",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/superAdmin/plans/access/resume", { userId });
      dispatch(showToast({ message: response.data.message || "User access resumed", variant: "success" }));
      return userId;
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || "Failed to resume access", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateActivePeriods = createAsyncThunk(
  "plans/updateActivePeriods",
  async (periods, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put("/superAdmin/plans/billing-periods", { periods });
      dispatch(showToast({ message: "Billing periods updated", variant: "success" }));
      return response.data.activePeriods;
    } catch (err) {
      dispatch(showToast({ message: "Failed to update billing periods", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

export const updateEmailReminderSchedule = createAsyncThunk(
  "plans/updateEmailReminderSchedule",
  async ({ userId, reminderDays, global: isGlobal }, { rejectWithValue, dispatch }) => {
    try {
      await api.put("/superAdmin/plans/email-reminders", { userId, reminderDays, global: isGlobal });
      dispatch(showToast({ message: "Email reminder schedule updated", variant: "success" }));
      return { userId, reminderDays, global: isGlobal };
    } catch (err) {
      dispatch(showToast({ message: "Failed to update email reminders", variant: "danger" }));
      return rejectWithValue(err.response?.data?.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const planSlice = createSlice({
  name: "plans",
  initialState: {
    plans: [],
    total: 0,
    loading: false,
    actionLoading: false,
    error: null,
    globalTrialDays: parseInt(localStorage.getItem("globalTrialDays") || "7", 10),
    globalReminderDays: localStorage.getItem("globalReminderDays") || "7,3,1",
    activePeriods: JSON.parse(localStorage.getItem("activeBillingPeriods") || "null") || ["monthly", "quarterly", "semiannual", "yearly"],
  },
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminPlans.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAdminPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.plans || [];
        state.total = action.payload.total || 0;
      })
      .addCase(fetchAdminPlans.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createPlan.pending, (state) => { state.actionLoading = true; })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.plan) state.plans.push(action.payload.plan);
        state.total += 1;
      })
      .addCase(createPlan.rejected, (state) => { state.actionLoading = false; })

      .addCase(updatePlan.pending, (state) => { state.actionLoading = true; })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload.plan) {
          const idx = state.plans.findIndex((p) => p._id === action.payload.plan._id);
          if (idx !== -1) state.plans[idx] = action.payload.plan;
        }
      })
      .addCase(updatePlan.rejected, (state) => { state.actionLoading = false; })

      .addCase(deletePlan.fulfilled, (state, action) => {
        state.plans = (state.plans || []).filter((p) => p._id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })

      .addCase(togglePlanStatus.fulfilled, (state, action) => {
        const idx = state.plans.findIndex((p) => p._id === action.payload._id);
        if (idx !== -1) state.plans[idx].isActive = action.payload.isActive;
      })

      .addCase(reorderPlans.fulfilled, (state, action) => {
        const idOrder = action.payload;
        const map = {};
        state.plans.forEach((p) => { map[p._id] = p; });
        state.plans = idOrder.map((id) => map[id]).filter(Boolean);
      })

      .addCase(updateGlobalTrialPeriod.fulfilled, (state, action) => {
        const days = action.payload; // durationDays returned from thunk
        if (days) {
          state.globalTrialDays = days;
          localStorage.setItem("globalTrialDays", String(days));
        }
      })

      .addCase(updateEmailReminderSchedule.fulfilled, (state, action) => {
        if (action.payload?.global && action.payload?.reminderDays) {
          const str = action.payload.reminderDays.join(",");
          state.globalReminderDays = str;
          localStorage.setItem("globalReminderDays", str);
        }
      })

      .addCase(fetchPlansConfig.fulfilled, (state, action) => {
        if (action.payload?.activePeriods?.length) {
          state.activePeriods = action.payload.activePeriods;
          localStorage.setItem("activeBillingPeriods", JSON.stringify(action.payload.activePeriods));
        }
      })

      .addCase(updateActivePeriods.fulfilled, (state, action) => {
        if (action.payload) {
          state.activePeriods = action.payload;
          localStorage.setItem("activeBillingPeriods", JSON.stringify(action.payload));
        }
      });
  },
});

export const { clearError } = planSlice.actions;
export default planSlice.reducer;
